import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EventsGrid } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ReportEvent } from '@/types/report';
import { useGrabbingCursor } from '@/hooks/useGrabbingCursor';
import type { ReportEventReordering } from '../hooks/useReportEventOrdering';
import { ReportEventPointerSensor } from '../utils/reportEventPointerSensor';
import { resolveDragEndMove } from '../utils/resolveDragEndMove';
import { ReportEventDragIndicator } from './ReportEventDragIndicator';
import {
    SortableReportEventCard,
    type ReportEventCardOptions,
} from './SortableReportEventCard';

interface SortableReportEventsGridProps {
    events: ReportEvent[];
    disabled: boolean;
    reordering: ReportEventReordering;
    renderEvent: (
        event: ReportEvent,
        options: ReportEventCardOptions,
    ) => ReactNode;
}

function sortableId(event: ReportEvent): string {
    return `${event.eventType}:${event.id}`;
}

export function SortableReportEventsGrid({
    events,
    disabled,
    reordering,
    renderEvent,
}: SortableReportEventsGridProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const [draggedEvent, setDraggedEvent] = useState<ReportEvent>();
    const { itemIds, eventsById } = useMemo(
        () => ({
            itemIds: events.map(sortableId),
            eventsById: new Map(
                events.map((event) => [sortableId(event), event]),
            ),
        }),
        [events],
    );
    const sensors = useSensors(
        // The distance threshold keeps a plain click on the card opening the
        // event instead of being swallowed as a drag.
        useSensor(ReportEventPointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useGrabbingCursor(draggedEvent !== undefined);

    const promptTextFor = (id: string | number) =>
        eventsById.get(String(id))?.promptText ?? '';

    const reorderLabelFor = (event: ReportEvent) =>
        t(
            reordering.isManualOrder
                ? 'detail.reorder.handle'
                : 'detail.reorder.handleWhileSorted',
            { name: event.promptText },
        );

    const handleDragStart = ({ active }: DragStartEvent) =>
        setDraggedEvent(eventsById.get(String(active.id)));

    const handleDragEnd = (dragEnd: DragEndEvent) => {
        setDraggedEvent(undefined);
        const move = resolveDragEndMove(dragEnd, itemIds);
        if (!move) {
            return;
        }
        const sourceEvent = eventsById.get(move.sourceId);
        const targetEvent = eventsById.get(move.targetId);
        if (!sourceEvent || !targetEvent) {
            return;
        }
        reordering.request(sourceEvent, targetEvent, move.placement);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragCancel={() => setDraggedEvent(undefined)}
            onDragEnd={handleDragEnd}
            accessibility={{
                announcements: {
                    onDragStart: ({ active }) =>
                        t('detail.reorder.pickedUp', {
                            name: promptTextFor(active.id),
                        }),
                    onDragOver: ({ over }) =>
                        over
                            ? t('detail.reorder.movedOver', {
                                  name: promptTextFor(over.id),
                              })
                            : undefined,
                    onDragEnd: ({ over }) =>
                        over
                            ? t('detail.reorder.dropped', {
                                  name: promptTextFor(over.id),
                              })
                            : t('detail.reorder.cancelled'),
                    onDragCancel: () => t('detail.reorder.cancelled'),
                },
            }}
        >
            <SortableContext items={itemIds} strategy={rectSortingStrategy}>
                <EventsGrid>
                    {events.map((event) => {
                        const id = sortableId(event);
                        return (
                            <SortableReportEventCard
                                key={id}
                                id={id}
                                reorderable={!disabled}
                                isReorderPending={reordering.isPending}
                                reorderLabel={reorderLabelFor(event)}
                            >
                                {(options) => renderEvent(event, options)}
                            </SortableReportEventCard>
                        );
                    })}
                </EventsGrid>
            </SortableContext>
            <DragOverlay className="rounded-[12px] outline outline-2 outline-grey-200 shadow-xl">
                {draggedEvent
                    ? renderEvent(draggedEvent, {
                          dragIndicator: (
                              <ReportEventDragIndicator
                                  label={reorderLabelFor(draggedEvent)}
                                  alwaysVisible
                              />
                          ),
                          isDragging: true,
                      })
                    : null}
            </DragOverlay>
        </DndContext>
    );
}
